import { FormEvent } from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { MapCanvas } from "@/components/MapCanvas"

import type { PageProps } from "@/types/inertia"

type Option = { id: number; label: string; key?: string }

export type PlaceFormRecord = {
  id: number | null
  destination_id: number | null
  category_id: number | null
  status: string
  name_en: string | null
  name_de: string | null
  slug_en: string | null
  slug_de: string | null
  summary_en: string | null
  summary_de: string | null
  body_en: string | null
  body_de: string | null
  meta_title_en: string | null
  meta_title_de: string | null
  meta_description_en: string | null
  meta_description_de: string | null
  schema_type: string | null
  latitude: number | null
  longitude: number | null
  address: string | null
  phone: string | null
  website_url: string | null
  booking_url: string | null
  beach_material: string | null
  sea_floor_material: string | null
  shade_level: string | null
  dogs_allowed: boolean | null
  fkk_allowed: boolean | null
  kid_friendly: boolean | null
  wheelchair_accessible: boolean | null
  toilets: boolean | null
  showers: boolean | null
  food_nearby: boolean | null
  parking_nearby: boolean | null
  entry_fee_note_en: string | null
  entry_fee_note_de: string | null
  opening_hours: Record<string, unknown>
  seasonal_note_en: string | null
  seasonal_note_de: string | null
}

type OpeningHours = Record<string, string>
type PlaceFormData = Record<string, string | OpeningHours>

const OPENING_HOURS_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const

const GENERAL_BOOLEAN_FACTS = [
  "wheelchair_accessible",
  "toilets",
  "food_nearby",
  "parking_nearby",
] as const

const BEACH_BOOLEAN_FACTS = [
  "dogs_allowed",
  "fkk_allowed",
  "kid_friendly",
  "showers",
] as const

const BEACH_DETAIL_OPTIONS = {
  beach_material: [
    { value: "fine_sand", label: "Fine sand" },
    { value: "sand", label: "Sand" },
    { value: "sand_and_pebbles", label: "Sand and pebbles" },
    { value: "sand_and_rock", label: "Sand and rock" },
    { value: "pebbles", label: "Pebbles" },
    { value: "pebbles_and_rock", label: "Pebbles and rock" },
    { value: "gravel", label: "Gravel" },
    { value: "rock", label: "Rock" },
    { value: "concrete", label: "Concrete" },
  ],
  sea_floor_material: [
    { value: "fine_sand", label: "Fine sand" },
    { value: "sand", label: "Sand" },
    { value: "sand_and_pebbles", label: "Sand and pebbles" },
    { value: "sand_and_rock", label: "Sand and rock" },
    { value: "pebbles", label: "Pebbles" },
    { value: "pebbles_and_rock", label: "Pebbles and rock" },
    { value: "gravel", label: "Gravel" },
    { value: "rock", label: "Rock" },
  ],
  shade_level: [
    { value: "none", label: "No shade" },
    { value: "very_little", label: "Very little shade" },
    { value: "some", label: "Some shade" },
    { value: "plenty", label: "Plenty of shade" },
  ],
} as const

type BeachDetailField = keyof typeof BEACH_DETAIL_OPTIONS

export function PlaceForm({
  place,
  destinationOptions,
  categoryOptions,
  statuses,
  schemaTypes,
  action,
  method,
  submitLabel,
}: {
  place: PlaceFormRecord
  destinationOptions: Option[]
  categoryOptions: Option[]
  statuses: string[]
  schemaTypes: string[]
  action: string
  method: "post" | "patch"
  submitLabel: string
}) {
  const { props } = usePage<PageProps>()
  const form = useForm<PlaceFormData>({
    destination_id: place.destination_id?.toString() ?? "",
    category_id: place.category_id?.toString() ?? "",
    status: place.status ?? "draft",
    name_en: place.name_en ?? "",
    name_de: place.name_de ?? "",
    slug_en: place.slug_en ?? "",
    slug_de: place.slug_de ?? "",
    summary_en: place.summary_en ?? "",
    summary_de: place.summary_de ?? "",
    body_en: place.body_en ?? "",
    body_de: place.body_de ?? "",
    meta_title_en: place.meta_title_en ?? "",
    meta_title_de: place.meta_title_de ?? "",
    meta_description_en: place.meta_description_en ?? "",
    meta_description_de: place.meta_description_de ?? "",
    schema_type: place.schema_type ?? "",
    latitude: place.latitude?.toString() ?? "",
    longitude: place.longitude?.toString() ?? "",
    address: place.address ?? "",
    phone: place.phone ?? "",
    website_url: place.website_url ?? "",
    booking_url: place.booking_url ?? "",
    beach_material: place.beach_material ?? "",
    sea_floor_material: place.sea_floor_material ?? "",
    shade_level: place.shade_level ?? "",
    dogs_allowed: booleanFormValue(place.dogs_allowed),
    fkk_allowed: booleanFormValue(place.fkk_allowed),
    kid_friendly: booleanFormValue(place.kid_friendly),
    wheelchair_accessible: booleanFormValue(place.wheelchair_accessible),
    toilets: booleanFormValue(place.toilets),
    showers: booleanFormValue(place.showers),
    food_nearby: booleanFormValue(place.food_nearby),
    parking_nearby: booleanFormValue(place.parking_nearby),
    entry_fee_note_en: place.entry_fee_note_en ?? "",
    entry_fee_note_de: place.entry_fee_note_de ?? "",
    opening_hours: Object.fromEntries(
      OPENING_HOURS_DAYS.map((day) => [day, String(place.opening_hours?.[day] ?? "")]),
    ),
    seasonal_note_en: place.seasonal_note_en ?? "",
    seasonal_note_de: place.seasonal_note_de ?? "",
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    method === "patch" ? form.patch(action) : form.post(action)
  }
  const selectedCategoryKey = categoryOptions.find(
    (category) => category.id.toString() === form.data.category_id,
  )?.key
  const openingHours = form.data.opening_hours as OpeningHours

  return (
    <form onSubmit={submit} className="mt-6 space-y-8">
      <Section title="Publishing">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Destination" error={props.errors?.destination_id}>
            <Select value={form.data.destination_id as string} onChange={(event) => form.setData("destination_id", event.target.value)}>
              <option value="">Choose destination</option>
              {destinationOptions.map((destination) => (
                <option key={destination.id} value={destination.id}>{destination.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Category" error={props.errors?.category_id}>
            <Select value={form.data.category_id as string} onChange={(event) => form.setData("category_id", event.target.value)}>
              <option value="">Choose category</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>{category.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Status" error={props.errors?.status}>
            <Select value={form.data.status as string} onChange={(event) => form.setData("status", event.target.value)}>
              {statuses.map((status) => (
                <option key={status} value={status}>{titleize(status)}</option>
              ))}
            </Select>
          </Field>
        </div>
      </Section>

      <Section title="Localized content">
        <div className="grid gap-4 lg:grid-cols-2">
          <LocalizedFields locale="en" form={form} errors={props.errors} />
          <LocalizedFields locale="de" form={form} errors={props.errors} />
        </div>
      </Section>

      <Section title="Search appearance" description="These values are stored per place. Leave a field blank to derive a safe default when the place is saved.">
        <div className="grid gap-4 lg:grid-cols-2">
          <SearchFields locale="en" form={form} errors={props.errors} />
          <SearchFields locale="de" form={form} errors={props.errors} />
        </div>
        <div className="mt-4 max-w-md">
          <Field
            label="Schema type"
            error={props.errors?.schema_type}
            description="Choose the Schema.org entity that most accurately describes this place."
          >
            <Select value={form.data.schema_type as string} onChange={(event) => form.setData("schema_type", event.target.value)}>
              <option value="">Derive from category</option>
              {schemaTypes.map((schemaType) => (
                <option key={schemaType} value={schemaType}>{schemaType}</option>
              ))}
            </Select>
          </Field>
        </div>
      </Section>

      <Section title="Location and links">
        <MapCanvas
          maptilerKey={props.maptiler_key}
          center={[
            numberOrFallback(form.data.longitude as string, 14.7569),
            numberOrFallback(form.data.latitude as string, 44.7569),
          ]}
          zoom={13}
          marker
          onPointChange={([longitude, latitude]) => {
            form.setData("longitude", longitude.toFixed(6))
            form.setData("latitude", latitude.toFixed(6))
          }}
          className="mb-4 h-[22rem]"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {["latitude", "longitude", "address", "phone", "website_url", "booking_url"].map((field) => (
            <Field key={field} label={titleize(field)} error={props.errors?.[field]}>
              <Input
                type={field === "latitude" || field === "longitude" ? "number" : "text"}
                step="any"
                value={form.data[field] as string}
                onChange={(event) => form.setData(field, event.target.value)}
              />
            </Field>
          ))}
        </div>
      </Section>

      <Section title="Facts" description="Leave a value unknown until an editor has reliable information.">
        {selectedCategoryKey === "beach" && (
          <FactGroup title="Beach details">
            <div className="grid gap-4 sm:grid-cols-3">
              {(Object.keys(BEACH_DETAIL_OPTIONS) as BeachDetailField[]).map((field) => (
                <StringFactSelect
                  key={field}
                  field={field}
                  form={form}
                  error={props.errors?.[field]}
                />
              ))}
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {BEACH_BOOLEAN_FACTS.map((field) => (
                <BooleanFactSelect key={field} field={field} form={form} error={props.errors?.[field]} />
              ))}
            </div>
          </FactGroup>
        )}

        <FactGroup title="Facilities and access">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {GENERAL_BOOLEAN_FACTS.map((field) => (
              <BooleanFactSelect key={field} field={field} form={form} error={props.errors?.[field]} />
            ))}
          </div>
        </FactGroup>

        <FactGroup title="Visitor notes">
          <div className="grid gap-4 lg:grid-cols-2">
            {["entry_fee_note_en", "entry_fee_note_de", "seasonal_note_en", "seasonal_note_de"].map((field) => (
              <Field key={field} label={titleize(field)} error={props.errors?.[field]}>
                <textarea
                  className="form-control form-control-textarea"
                  value={form.data[field] as string}
                  onChange={(event) => form.setData(field, event.target.value)}
                />
              </Field>
            ))}
          </div>
        </FactGroup>

        <FactGroup title="Opening hours" description="Use short visitor-facing text, for example 09:00–17:00 or Closed.">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {OPENING_HOURS_DAYS.map((day) => (
              <Field key={day} label={titleize(day)} error={props.errors?.opening_hours}>
                <Input
                  value={openingHours[day]}
                  placeholder="Unknown"
                  onChange={(event) => form.setData("opening_hours", { ...openingHours, [day]: event.target.value })}
                />
              </Field>
            ))}
          </div>
        </FactGroup>
      </Section>

      <div className="flex items-center gap-3 border-t border-hairline pt-5">
        <Button type="submit" disabled={form.processing}>{submitLabel}</Button>
        <Button asChild variant="secondary"><Link href="/admin/places">Cancel</Link></Button>
      </div>
    </form>
  )
}

function LocalizedFields({
  locale,
  form,
  errors,
}: {
  locale: "en" | "de"
  form: ReturnType<typeof useForm<PlaceFormData>>
  errors: Record<string, string>
}) {
  return (
    <div className="space-y-4 rounded-md border border-hairline p-4">
      <h3>{locale.toUpperCase()}</h3>
      {["name", "slug", "summary", "body"].map((field) => (
        <Field key={field} label={titleize(field)} error={errors?.[`${field}_${locale}`]}>
          {field === "summary" || field === "body" ? (
            <textarea
              className="form-control form-control-textarea"
              value={form.data[`${field}_${locale}`] as string}
              onChange={(event) => form.setData(`${field}_${locale}`, event.target.value)}
            />
          ) : (
            <Input
              value={form.data[`${field}_${locale}`] as string}
              onChange={(event) => form.setData(`${field}_${locale}`, event.target.value)}
            />
          )}
        </Field>
      ))}
    </div>
  )
}

function SearchFields({
  locale,
  form,
  errors,
}: {
  locale: "en" | "de"
  form: ReturnType<typeof useForm<PlaceFormData>>
  errors: Record<string, string>
}) {
  const titleField = `meta_title_${locale}`
  const descriptionField = `meta_description_${locale}`

  return (
    <div className="space-y-4 rounded-md border border-hairline p-4">
      <h3>{locale.toUpperCase()}</h3>
      <Field
        label="Search title"
        error={errors?.[titleField]}
        description={`${String(form.data[titleField] ?? "").length}/60 characters`}
      >
        <Input
          maxLength={60}
          value={form.data[titleField] as string}
          onChange={(event) => form.setData(titleField, event.target.value)}
        />
      </Field>
      <Field
        label="Search description"
        error={errors?.[descriptionField]}
        description={`${String(form.data[descriptionField] ?? "").length}/160 characters · target 120–160`}
      >
        <textarea
          className="form-control form-control-textarea"
          maxLength={160}
          value={form.data[descriptionField] as string}
          onChange={(event) => form.setData(descriptionField, event.target.value)}
        />
      </Field>
    </div>
  )
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section>
      <h2>{title}</h2>
      {description && <p className="mt-1">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  )
}

function FactGroup({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <fieldset className="mt-5 rounded-md border border-hairline p-4 first:mt-0">
      <legend className="px-1">{title}</legend>
      {description && <p className="form-description mb-4">{description}</p>}
      {children}
    </fieldset>
  )
}

function BooleanFactSelect({
  field,
  form,
  error,
}: {
  field: string
  form: ReturnType<typeof useForm<PlaceFormData>>
  error?: string
}) {
  return (
    <Field label={titleize(field)} error={error}>
      <Select value={form.data[field] as string} onChange={(event) => form.setData(field, event.target.value)}>
        <option value="">Unknown</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Select>
    </Field>
  )
}

function StringFactSelect({
  field,
  form,
  error,
}: {
  field: BeachDetailField
  form: ReturnType<typeof useForm<PlaceFormData>>
  error?: string
}) {
  const value = form.data[field] as string
  const options = BEACH_DETAIL_OPTIONS[field]
  const hasLegacyValue = value !== "" && !options.some((option) => option.value === value)

  return (
    <Field label={titleize(field)} error={error}>
      <Select value={value} onChange={(event) => form.setData(field, event.target.value)}>
        <option value="">Unknown</option>
        {hasLegacyValue && <option value={value}>{titleize(value)} (existing value)</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Select>
    </Field>
  )
}

function Field({
  label,
  error,
  description,
  children,
}: {
  label: string
  error?: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <label className="space-y-2">
      <span>{label}</span>
      {children}
      {description && !error && <span className="block text-xs text-ink-muted">{description}</span>}
      {error && <span className="block text-sm text-danger-display">{error}</span>}
    </label>
  )
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function numberOrFallback(value: string, fallback: number) {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function booleanFormValue(value: boolean | null) {
  return value === null ? "" : value.toString()
}
